import React, { useEffect, useMemo, useState } from "react";

import { getEventOwnerLine } from "../../utils/ownerLine";

const PAGE_SIZE = 10;

export default function PlaybackCameraList({
  cameras,
  loadingCameras,
  selectedCameraId,
  onSelectedCameraIdChange,
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // 검색 필터 (카메라 이름 / CamIdx / 스트림키)
  const filtered = useMemo(() => {
    const list = Array.isArray(cameras) ? cameras : [];
    const q = String(search || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((cam) => {
      const name = String(cam?.camName ?? "").toLowerCase();
      const id = String(cam?.id ?? "").toLowerCase();
      const key = String(cam?.streamKey ?? "").toLowerCase();
      const no = String(cam?.camNo ?? "").toLowerCase();
      return name.includes(q) || id.includes(q) || key.includes(q) || no.includes(q);
    });
  }, [cameras, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const paged = useMemo(() => {
    const safePage = Math.min(Math.max(page, 1), pageCount);
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page, pageCount]);

  // 검색어 바뀌면 1페이지로
  useEffect(() => {
    setPage(1);
  }, [search]);

  // 페이지가 범위를 벗어나면 보정
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <div className="col-span-12 flex min-h-0 flex-col rounded-2xl bg-white p-3 shadow-md dark:bg-[#1F2A3B] lg:col-span-2 lg:h-full lg:overflow-hidden">
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
        <h4 className="text-sm font-bold sm:text-base">카메라 목록</h4>
        <div className="text-[11px] text-gray-500 dark:text-gray-400">
          {filtered.length}대
        </div>
      </div>

      <div className="mb-2 shrink-0">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="카메라 이름 / 번호 검색"
          className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-[#0F172A] dark:text-white"
        />
      </div>

      <div className="grid auto-rows-max grid-cols-1 content-start gap-1.5 sm:grid-cols-2 lg:min-h-0 lg:flex-1 lg:grid-cols-1 lg:overflow-y-auto">
        {loadingCameras ? (
          <div className="text-sm text-gray-500 dark:text-gray-300">
            카메라 목록 불러오는 중...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-300">
            {search ? "검색 결과가 없습니다." : "등록된 카메라가 없습니다."}
          </div>
        ) : (
          paged.map((cam) => {
            const active = String(cam.id) === String(selectedCameraId);

            return (
              <button
                key={cam.id}
                type="button"
                onClick={() => onSelectedCameraIdChange(String(cam.id))}
                className={`w-full shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${
                  active
                    ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-[#0F172A]"
                    : "border-gray-200 hover:border-blue-400 dark:border-gray-700"
                }`}
              >
                <div className="line-clamp-1 text-sm font-semibold">
                  {cam.camName}
                </div>
                {/* "지점 / 팀 / 이름 / 차량번호".
                    값이 하나도 없으면 기존 CAM 번호 표기로 돌아간다. */}
                {getEventOwnerLine(cam) || cam.camNo ? (
                  <div className="mt-1 line-clamp-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {getEventOwnerLine(cam) || `CAM ${cam.camNo}`}
                  </div>
                ) : null}
              </button>
            );
          })
        )}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="mt-2 flex shrink-0 items-center justify-between gap-2 border-t border-gray-100 pt-2 dark:border-white/10">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
          >
            ◀ 이전
          </button>
          <div className="min-w-[56px] text-center text-sm font-semibold text-gray-600 dark:text-gray-300">
            {page} / {pageCount}
          </div>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={page >= pageCount}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:cursor-default disabled:opacity-40 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15"
          >
            다음 ▶
          </button>
        </div>
      )}
    </div>
  );
}
